/**
 * Product mode showcase: tab-style mode picker, optional autoplay (pauses on hover/focus-in).
 */
class ProductModeShowcase extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[data-pms-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-pms-panel]'));
    if (!this.tabs.length || !this.panels.length) return;

    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.autoplayEnabled =
      this.dataset.autoplay === 'true' && !this.reduceMotion;
    this.intervalMs = Math.min(
      Math.max(parseInt(this.dataset.intervalMs || '4000', 10) || 4000, 2500),
      12000
    );

    this.current = 0;
    this.timer = null;

    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.select(i));
      tab.addEventListener('keydown', (e) => this.onTabKeydown(e, i));
    });

    this.addEventListener('mouseenter', () => this.pauseAutoplay());
    this.addEventListener('mouseleave', () => this.maybeStartAutoplay());
    this.addEventListener('focusin', () => this.pauseAutoplay());
    this.addEventListener('focusout', (e) => {
      if (!this.contains(e.relatedTarget)) this.maybeStartAutoplay();
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.pauseAutoplay();
      else this.maybeStartAutoplay();
    });

    this.select(0, false);
    this.maybeStartAutoplay();
  }

  disconnectedCallback() {
    this.pauseAutoplay();
  }

  onTabKeydown(e, index) {
    const last = this.tabs.length - 1;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      this.select(Math.min(index + 1, last));
      this.tabs[Math.min(index + 1, last)].focus();
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      this.select(Math.max(index - 1, 0));
      this.tabs[Math.max(index - 1, 0)].focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      this.select(0);
      this.tabs[0].focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      this.select(last);
      this.tabs[last].focus();
    }
  }

  select(index, restartAutoplay = true) {
    const i = ((index % this.tabs.length) + this.tabs.length) % this.tabs.length;
    this.current = i;

    this.tabs.forEach((tab, j) => {
      const selected = j === i;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.tabIndex = selected ? 0 : -1;
      tab.classList.toggle('is-active', selected);
    });

    this.panels.forEach((panel, j) => {
      const on = j === i;
      panel.toggleAttribute('hidden', !on);
      panel.classList.toggle('is-active', on);
    });

    if (restartAutoplay) {
      this.pauseAutoplay();
      this.maybeStartAutoplay();
    }
  }

  pauseAutoplay() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  maybeStartAutoplay() {
    if (!this.autoplayEnabled || this.tabs.length < 2) return;
    this.pauseAutoplay();
    this.timer = window.setInterval(() => {
      this.select(this.current + 1, false);
    }, this.intervalMs);
  }
}

if (!customElements.get('product-mode-showcase')) {
  customElements.define('product-mode-showcase', ProductModeShowcase);
}
